import { Link } from "react-router-dom";
import { trackPrimaryCta, trackSecondaryCta } from "@/lib/analytics";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import heroArch from "@/assets/visual-lab/hero-arch.jpg";
import heroArchMobile from "@/assets/visual-lab/hero-arch-mobile.jpg";
import heroArchEn from "@/assets/visual-lab/hero-arch-en.jpg";
import heroArchMobileEn from "@/assets/visual-lab/hero-arch-mobile-en.jpg";
import { EASE, routesFor, type Lang } from "../primitives";

const enter = (delay: number, reduced: boolean | null) =>
  reduced
    ? {}
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, delay, ease: EASE },
      };

const t = {
  es: {
    kicker: "Luis ROC · Consultoría SEO y crecimiento digital",
    h1a: "De estar en internet",
    h1b: "a ser elegido",
    lede:
      "SEO técnico, búsqueda local y Google Ads para fabricantes y empresas de servicios que buscan oportunidades de negocio en Estados Unidos.",
    cta1: "Solicitar diagnóstico SEO",
    cta2: "Ver proyectos",
    micro: "Visibilidad · Comprensión · Decisión",
    artAlt:
      "Composición escultórica de planos alineados: descubrir, investigar, comparar y decidir.",
    aria: "Presentación",
    bridgeKicker: "En un entorno más complejo",
    bridgeA: "La búsqueda evoluciona.",
    bridgeB: "Tu estrategia también debe hacerlo.",
    bridgeText:
      "Hoy, tus clientes te encuentran en Google, en herramientas de IA, en mapas, en marketplaces y en búsquedas de marca. La diferencia no está en estar en todos lados, sino en ser la opción elegida.",
  },
  en: {
    kicker: "Luis ROC · SEO and digital growth consulting",
    h1a: "From being online",
    h1b: "to being chosen",
    lede:
      "Technical SEO, local search and Google Ads for manufacturers and service companies pursuing business opportunities in the United States.",
    cta1: "Request SEO diagnosis",
    cta2: "See projects",
    micro: "Visibility · Understanding · Decision",
    artAlt:
      "Sculptural composition of aligned planes representing discover, research, compare and decide.",
    aria: "Introduction",
    bridgeKicker: "In a more complex environment",
    bridgeA: "Search keeps evolving.",
    bridgeB: "Your strategy has to evolve too.",
    bridgeText:
      "Your customers find you on Google, in AI tools, on maps, in marketplaces and through branded searches. The difference is not being everywhere — it is being the option they choose.",
  },
} as const;

/**
 * Hero arquitectónico. El arte es una imagen plana entregada por el cliente
 * (render escultórico Descubrir · Investigar · Comparar · Decidir). No se
 * recrea con CSS ni SVG y no se anima por planos: solo entra el contenido.
 * Cada idioma usa su propia versión del mismo arte con los rótulos grabados
 * traducidos (Discover · Research · Compare · Decide en EN).
 */
export const SceneHero = ({ language = "es" as Lang }: { language?: Lang }) => {
  const reduced = useReducedMotion();
  const c = t[language];
  const artDesktop = language === "en" ? heroArchEn : heroArch;
  const artMobile = language === "en" ? heroArchMobileEn : heroArchMobile;
  const routes = routesFor(language);

  return (
    <section className="relative overflow-hidden bg-[color:var(--v3-mineral)]" aria-label={c.aria}>
      <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden>
        <img
          src={artDesktop}
          alt=""
          className="h-full w-full object-cover object-[78%_bottom]"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-y-0 left-0 w-[62%] bg-[linear-gradient(90deg,rgba(238,241,246,0.94)_0%,rgba(238,241,246,0.82)_46%,rgba(238,241,246,0)_100%)]" />
      </div>

      <div className="v3-shell relative">
        <div className="flex min-h-[54vh] max-w-[46rem] flex-col justify-center py-14 md:min-h-[68vh] lg:min-h-[74vh] lg:max-w-[40rem] lg:py-20">
          <motion.p className="v3-kicker" {...enter(0.05, reduced)}>
            {c.kicker}
          </motion.p>

          <motion.h1
            className="v3-display mt-6"
            style={{ fontSize: "clamp(2.25rem, 4.05vw, 3.8rem)" }}
            {...enter(0.13, reduced)}
          >
            {c.h1a}
            <br />
            {c.h1b}
            <span style={{ color: "var(--v3-blue)" }}>.</span>
          </motion.h1>

          <motion.p className="v3-lede mt-7 max-w-[30ch]" {...enter(0.21, reduced)}>
            {c.lede}
          </motion.p>

          <motion.div className="mt-10 flex flex-wrap items-center gap-7" {...enter(0.29, reduced)}>
            <Link
              to={routes.seoDiagnosis}
              className="v3-btn"
              onClick={() => trackPrimaryCta(c.cta1, 'home_hero')}
            >
              {c.cta1}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <Link
              to={routes.caseStudies}
              className="v3-btn-ghost"
              onClick={() => trackSecondaryCta(c.cta2, 'home_hero')}
            >
              {c.cta2}
            </Link>
          </motion.div>

          <motion.div className="mt-12 flex items-center gap-4" {...enter(0.37, reduced)}>
            <span aria-hidden className="h-px w-14 bg-[color:var(--v3-blue)]" />
            <span className="v3-micro">{c.micro}</span>
          </motion.div>
        </div>
      </div>

      <div className="md:hidden">
        <img
          src={artMobile}
          alt={c.artAlt}
          className="h-[46vh] min-h-[300px] w-full object-cover object-[center_62%]"
          loading="eager"
          decoding="async"
        />
      </div>
    </section>
  );
};

/** Transición hacia la siguiente escena: declaración editorial sobre fondo blanco. */
export const SceneHeroBridge = ({ language = "es" as Lang }: { language?: Lang }) => {
  const reduced = useReducedMotion();
  const c = t[language];
  return (
    <section className="v3-section bg-white" aria-label={c.bridgeA}>
      <div className="v3-shell grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <p className="v3-micro">{c.bridgeKicker}</p>
          <motion.h2
            className="v3-display mt-6"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.1rem)" }}
            initial={reduced ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            {c.bridgeA}
            <br />
            {c.bridgeB}
          </motion.h2>
        </div>
        <div className="lg:col-span-5 lg:border-l lg:border-[color:var(--v3-line)] lg:pl-10">
          <p className="v3-lede max-w-none">{c.bridgeText}</p>
        </div>
      </div>
    </section>
  );
};
